import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Code } from 'lucide-react';
import { Project } from '../types';

interface ProjectDetailOverlayProps {
  project: Project;
}

const ProjectDetailOverlay: React.FC<ProjectDetailOverlayProps> = ({ project }) => {
  const navigate = useNavigate();

  return (
    <div className="absolute inset-0 z-[200] bg-zinc-950 flex flex-col animate-in fade-in zoom-in-95 duration-500 h-full overflow-hidden">
      <div className="flex justify-between items-center px-6 md:px-8 py-4 md:py-6 border-b border-white/5 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-10">
        <button
          onClick={() => navigate('/work')}
          className="flex items-center space-x-2 text-zinc-400 hover:text-white transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <div className="flex items-center space-x-4">
          {project.link && (
            <a href={project.link} className="px-3 md:px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-[10px] md:text-xs font-bold rounded-lg flex items-center space-x-2 transition-all">
              <span>Live</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 md:px-20 py-8 md:py-12 no-scrollbar pb-32 md:pb-12">
        <div className="max-w-5xl mx-auto">
          <header className="mb-10 md:mb-16">
            <div className="flex items-center space-x-3 mb-4">
              <span className="text-red-500 font-mono text-xs md:sm font-bold tracking-widest">{project.year}</span>
              <div className="w-8 md:w-12 h-[1px] bg-white/10"></div>
              <span className="text-zinc-500 text-[10px] uppercase tracking-[0.2em] font-medium">Case Study</span>
            </div>
            <h1 className="text-4xl md:text-8xl font-black tracking-tighter text-white mb-6 md:mb-8 leading-tight md:leading-none">
              {project.title}
            </h1>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {project.tags.map(tag => (
                <span key={tag} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-zinc-300 text-[10px] md:text-xs font-mono">
                  {tag}
                </span>
              ))}
            </div>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-16">
            <div className="lg:col-span-6 space-y-8 md:space-y-12 lg:space-y-0 lg:flex lg:flex-col lg:h-[32rem] lg:justify-between lg:gap-8">
              <section>
                <h3 className="text-zinc-500 text-[10px] font-bold tracking-widest uppercase mb-4">Overview</h3>
                <p className="text-lg md:text-xl text-zinc-300 leading-relaxed font-light italic">
                  "{project.fullDescription}"
                </p>
              </section>

              <div className="p-6 md:p-8 rounded-2xl border border-white/5 bg-zinc-900/20">
                <h3 className="text-white text-sm font-bold mb-4 flex items-center space-x-2">
                  <Code className="w-4 h-4 text-red-500" />
                  <span>Tech Stack</span>
                </h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-4">
                  {project.tags.map(tag => (
                    <div key={tag} className="flex items-center space-x-2">
                      <div className="w-1 h-1 rounded-full bg-red-500"></div>
                      <span className="text-zinc-400 text-xs md:text-sm">{tag}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 space-y-6 md:space-y-8">
              <div className="rounded-2xl md:rounded-3xl overflow-hidden border border-white/5 group">
                <div className="w-full h-64 sm:h-80 md:h-96 lg:h-[32rem]">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover grayscale-0 group-hover:scale-105 transition-transform duration-1000"
                  />
                </div>
              </div>

            </div>
          </div>

          <div className="mt-10 md:mt-16">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8">
              <section className="p-6 md:p-10 rounded-2xl md:rounded-3xl bg-zinc-900/30 border border-white/5">
                <div className="flex items-center justify-between gap-6 mb-4 md:mb-6">
                  <h3 className="text-red-500 text-[10px] font-bold tracking-widest uppercase">The Challenge</h3>
                  <div className="hidden sm:block flex-1 h-px bg-white/5"></div>
                </div>
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{project.challenge}</p>
              </section>
              <section className="p-6 md:p-10 rounded-2xl md:rounded-3xl bg-emerald-500/5 border border-emerald-500/10">
                <div className="flex items-center justify-between gap-6 mb-4 md:mb-6">
                  <h3 className="text-emerald-500 text-[10px] font-bold tracking-widest uppercase">The Solution</h3>
                  <div className="hidden sm:block flex-1 h-px bg-white/5"></div>
                </div>
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{project.solution}</p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetailOverlay;
